// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * InventoryOverviewGrid container component.
 * Orchestrates the grid layout and renders the overview data using flexbox with dynamic column spans.
 */
import { ScrollIndicator } from '@/components/scroll-indicator';
import { AssetFunctionalStatus } from '@/constants/asset-functional-status';
import { useHorizontalScrollIndicator } from '@/hooks/use-horizontal-scroll-indicator';
import { CollapsibleSection } from '@/inventory/components/overview/components/collapsible-section';
import { LocationHeader } from '@/inventory/components/overview/components/location-header';
import { ProductRow } from '@/inventory/components/overview/components/product-row';
import { TotalRows } from '@/inventory/components/overview/components/total-rows';
import {
  INVENTORY_OVERVIEW_COLORS,
  INVENTORY_OVERVIEW_DIMENSIONS,
} from '@/inventory/components/overview/constants';
import {
  cellStyles,
  gridRowStyles,
  headerBackgroundStyles,
} from '@/inventory/components/overview/styles';
import { InventoryOverviewData } from '@/inventory/components/overview/types';
import { Box, Typography } from '@mui/material';
import { useRef } from 'react';

interface InventoryOverviewGridProps {
  data: InventoryOverviewData;
  onAssetClick?: (
    productKey: string,
    locationKey: string,
    status: AssetFunctionalStatus
  ) => void;
  showScrappedAssets?: boolean;
  hideZeroes?: boolean;
  isInventoryExpanded: boolean;
  isNonInventoryExpanded: boolean;
  isInTransitExpanded: boolean;
  onToggleInventory: () => void;
  onToggleNonInventory: () => void;
  onToggleInTransit: () => void;
}

/**
 * InventoryOverviewGrid container component using flexbox layout with dynamic spans
 */
export function InventoryOverviewGrid(props: InventoryOverviewGridProps) {
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const dataScrollRef = useRef<HTMLDivElement>(null);
  const scrollIndicator = useHorizontalScrollIndicator(headerScrollRef);

  const toggleInventory = () => props.onToggleInventory();
  const toggleNonInventory = () => props.onToggleNonInventory();
  const toggleInTransit = () => props.onToggleInTransit();

  // Sync scroll between header and data containers
  const handleHeaderScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const headerElement = event.currentTarget;
    const dataElement = dataScrollRef.current;

    if (dataElement && headerElement.scrollLeft !== dataElement.scrollLeft) {
      dataElement.scrollLeft = headerElement.scrollLeft;
    }
  };

  const handleDataScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const dataElement = event.currentTarget;
    const headerElement = headerScrollRef.current;

    if (headerElement && dataElement.scrollLeft !== headerElement.scrollLeft) {
      headerElement.scrollLeft = dataElement.scrollLeft;
    }
  };

  const handleHorizontalScroll = () => {
    if (dataScrollRef.current === null || headerScrollRef.current === null) {
      return;
    }

    const scrollLeft =
      dataScrollRef.current.scrollWidth - dataScrollRef.current.clientWidth;

    dataScrollRef.current.scrollTo({
      left: scrollLeft,
      behavior: 'smooth',
    });

    headerScrollRef.current.scrollTo({
      left: scrollLeft,
      behavior: 'smooth',
    });

    scrollIndicator.hide();
  };

  // Calculate column counts for dynamic spanning
  const inventoryColumnCount = props.isInventoryExpanded
    ? props.data.inventoryLocations.length + 1
    : 1;
  const nonInventoryColumnCount = props.isNonInventoryExpanded
    ? props.data.nonInventoryLocations.length + 1
    : 1;
  const inTransitColumnCount = props.isInTransitExpanded ? 3 : 1; // To inventory + to non-inventory + total

  // Calculate grid template columns - only include actual visible columns
  let gridTemplate = '225px'; // Device items column

  // Add inventory columns
  if (props.isInventoryExpanded && props.data.inventoryLocations.length > 0) {
    gridTemplate += ` repeat(${props.data.inventoryLocations.length}, ${INVENTORY_OVERVIEW_DIMENSIONS.CELL_WIDTH})`;
  }
  gridTemplate += ` 175px`; // Inventory total

  // Add non-inventory columns
  if (
    props.isNonInventoryExpanded &&
    props.data.nonInventoryLocations.length > 0
  ) {
    gridTemplate += ` repeat(${props.data.nonInventoryLocations.length}, ${INVENTORY_OVERVIEW_DIMENSIONS.CELL_WIDTH})`;
  }
  gridTemplate += ` 175px`; // Non-inventory total

  // Add in-transit columns
  if (props.isInTransitExpanded) {
    gridTemplate += ` repeat(2, ${INVENTORY_OVERVIEW_DIMENSIONS.CELL_WIDTH})`; // To inventory + to non-inventory
  }
  gridTemplate += ` 175px`; // In transit total

  // Add final columns with flexible space
  // Grand Total is now 175px - swapped with Device items column
  gridTemplate += ` 1fr 175px`; // Flexible space + Grand Total

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Fixed Headers - Outside scrolling context */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          backgroundColor: 'white',
        }}
      >
        <Box
          ref={headerScrollRef}
          onScroll={handleHeaderScroll}
          sx={{
            width: '100%',
            overflowX: 'auto',
            scrollbarWidth: 'none', // Firefox
            '&::-webkit-scrollbar': {
              display: 'none', // Chrome, Safari, Edge
            },
          }}
        >
          <Box sx={{ width: '100%', minWidth: 'fit-content' }}>
            {/* Header Row 1 - Sticky to viewport */}
            <Box
              sx={{
                ...gridRowStyles.headerRow,
                gridTemplateColumns: gridTemplate,
                borderBottom: 'none',
                backgroundColor: 'white',
              }}
            >
              {/* Device items Header */}
              <Box
                sx={{
                  backgroundColor: 'white',
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                  minHeight: '4rem', // Ensure consistent 2-line height
                }}
              />

              {/* Inventory Locations Header - spans multiple columns */}
              <Box
                sx={{
                  ...cellStyles.header,
                  backgroundColor:
                    INVENTORY_OVERVIEW_COLORS.INVENTORY.TOTAL_BACKGROUND,
                  gridColumn: `span ${inventoryColumnCount}`,
                  p: 0,
                  minHeight: '4rem', // Ensure consistent 2-line height
                }}
              >
                <CollapsibleSection
                  title={`Inventory (${props.data.inventoryLocations.length}\u00A0locations)`}
                  isExpanded={props.isInventoryExpanded}
                  onToggle={toggleInventory}
                />
              </Box>

              {/* Non-inventory Locations Header - spans multiple columns */}
              <Box
                sx={{
                  ...cellStyles.header,
                  backgroundColor:
                    INVENTORY_OVERVIEW_COLORS.NON_INVENTORY.TOTAL_BACKGROUND,
                  gridColumn: `span ${nonInventoryColumnCount}`,
                  p: 0,
                  minHeight: '4rem', // Ensure consistent 2-line height
                }}
              >
                <CollapsibleSection
                  title={`Non-inventory (${props.data.nonInventoryLocations.length}\u00A0locations)`}
                  isExpanded={props.isNonInventoryExpanded}
                  onToggle={toggleNonInventory}
                />
              </Box>

              {/* In Transit Header - spans multiple columns */}
              <Box
                sx={{
                  ...cellStyles.header,
                  backgroundColor:
                    INVENTORY_OVERVIEW_COLORS.IN_TRANSIT.TOTAL_BACKGROUND,
                  gridColumn: `span ${inTransitColumnCount}`,
                  p: 0,
                  minHeight: '4rem', // Ensure consistent 2-line height
                }}
              >
                <CollapsibleSection
                  title="In transit"
                  isExpanded={props.isInTransitExpanded}
                  onToggle={toggleInTransit}
                />
              </Box>

              {/* Empty flexible space */}
              <Box sx={{ ...cellStyles.header, minHeight: '4rem' }} />

              {/* Total Header - Sticky right aligned */}
              <Box
                sx={{
                  ...cellStyles.header,
                  justifyContent: 'flex-start',
                  p: 2,
                  position: 'sticky',
                  right: 0,
                  backgroundColor: 'white',
                  zIndex: 2,
                  minHeight: '4rem', // Ensure consistent 2-line height
                }}
              ></Box>
            </Box>

            {/* Header Row 2 - Sub headers */}
            <Box
              sx={{
                ...gridRowStyles.subHeaderRow,
                gridTemplateColumns: gridTemplate,
                backgroundColor: 'white',
              }}
            >
              {/* Device items cell */}
              <Box sx={cellStyles.stickyHeaderProduct}>
                <Typography variant="subtitle2">Device items</Typography>
              </Box>

              {/* Individual inventory location headers */}
              {props.isInventoryExpanded &&
                props.data.inventoryLocations.map((location) => (
                  <Box
                    key={location.location.key}
                    sx={{
                      ...cellStyles.subHeader,
                      backgroundColor:
                        INVENTORY_OVERVIEW_COLORS.INVENTORY.LOCATION_BACKGROUND,
                    }}
                  >
                    <LocationHeader
                      location={location.location}
                      locationOwner="company"
                    />
                  </Box>
                ))}

              {/* Inventory Total Header */}
              <Box
                sx={{
                  ...headerBackgroundStyles.inventoryTotal,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'rgba(0, 0, 0, 0.87)',
                  }}
                >
                  Inventory total
                </Typography>
              </Box>

              {/* Individual non-inventory location headers */}
              {props.isNonInventoryExpanded &&
                props.data.nonInventoryLocations.map((location) => (
                  <Box
                    key={location.location.key}
                    sx={{
                      ...cellStyles.subHeader,
                      backgroundColor:
                        INVENTORY_OVERVIEW_COLORS.NON_INVENTORY
                          .LOCATION_BACKGROUND,
                    }}
                  >
                    <LocationHeader
                      location={location.location}
                      locationOwner="customer"
                    />
                  </Box>
                ))}

              {/* Non-inventory Total Header */}
              <Box sx={headerBackgroundStyles.nonInventoryTotal}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'rgba(0, 0, 0, 0.87)',
                  }}
                >
                  Non-inventory total
                </Typography>
              </Box>

              {/* Individual in-transit sub-headers */}
              {props.isInTransitExpanded && (
                <>
                  <Box
                    sx={{
                      ...headerBackgroundStyles.inTransit,
                      backgroundColor:
                        INVENTORY_OVERVIEW_COLORS.IN_TRANSIT
                          .LOCATION_BACKGROUND,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: INVENTORY_OVERVIEW_COLORS.IN_TRANSIT.TEXT,
                      }}
                    >
                      In transit to inventory
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      ...headerBackgroundStyles.inTransit,
                      backgroundColor:
                        INVENTORY_OVERVIEW_COLORS.IN_TRANSIT
                          .LOCATION_BACKGROUND,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: INVENTORY_OVERVIEW_COLORS.IN_TRANSIT.TEXT,
                      }}
                    >
                      In transit to the field
                    </Typography>
                  </Box>
                </>
              )}

              {/* In Transit Total Header */}
              <Box sx={headerBackgroundStyles.inTransitTotal}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'rgba(0, 0, 0, 0.87)',
                  }}
                >
                  In transit total
                </Typography>
              </Box>

              {/* Empty flexible space */}
              <Box sx={{ backgroundColor: 'grey.50' }} />

              {/* Total Sub Header - Sticky right aligned */}
              <Box
                sx={{
                  ...headerBackgroundStyles.total,
                  backgroundColor: 'grey.100',
                  position: 'sticky',
                  right: 0,
                  zIndex: 2,
                }}
              >
                <Typography variant="subtitle2">Total</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <ScrollIndicator
          label=""
          isVisible={scrollIndicator.isVisible}
          onClick={handleHorizontalScroll}
          direction="horizontal"
          sx={{
            position: 'absolute',
            right: '175px',
            bottom: '20px',
            transform: 'translateX(50%)',
            zIndex: 10,
          }}
        />
      </Box>

      {/* Data Content - Separate from headers, allows page scrolling */}
      <Box
        ref={dataScrollRef}
        onScroll={handleDataScroll}
        sx={{
          position: 'relative',
          width: '100%',
          overflowX: 'auto',
        }}
      >
        <Box sx={{ width: '100%', minWidth: 'fit-content' }}>
          {/* Data Rows - Product rows */}
          {props.data.productRows.map((productRow) => (
            <ProductRow
              key={productRow.product.key}
              productRow={productRow}
              inventoryLocations={props.data.inventoryLocations}
              nonInventoryLocations={props.data.nonInventoryLocations}
              isInventoryExpanded={props.isInventoryExpanded}
              isNonInventoryExpanded={props.isNonInventoryExpanded}
              isInTransitExpanded={props.isInTransitExpanded}
              onAssetClick={props.onAssetClick}
              gridTemplate={gridTemplate}
              showScrappedAssets={props.showScrappedAssets ?? false}
              hideZeroes={props.hideZeroes ?? false}
            />
          ))}

          {/* Totals row */}
          <TotalRows
            totals={props.data.totals}
            inventoryLocations={props.data.inventoryLocations}
            nonInventoryLocations={props.data.nonInventoryLocations}
            isInventoryExpanded={props.isInventoryExpanded}
            isNonInventoryExpanded={props.isNonInventoryExpanded}
            isInTransitExpanded={props.isInTransitExpanded}
            gridTemplate={gridTemplate}
            showScrappedAssets={props.showScrappedAssets ?? false}
            hideZeroes={props.hideZeroes ?? false}
          />
        </Box>
      </Box>
    </Box>
  );
}
